import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getProducts, createProduct, updateProduct, deleteProduct, addToStock, getProductByBarcode } from '../lib/api';
import { Plus, Search, Edit2, Trash2, Package, ScanBarcode, Camera, Warehouse, Store, BookmarkCheck, Tags, ArrowRight, X, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { BarcodeScanner } from '../components/BarcodeScanner';
import api from '../lib/api';

// Generate random barcode
const generateBarcode = () => {
    const timestamp = Date.now().toString().slice(-8);
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, '0');
    return `INT${timestamp}${random}`;
};

export const Products = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const [products, setProducts] = useState([]);
    const [productLocations, setProductLocations] = useState({});
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [currentPallet, setCurrentPallet] = useState('1');
    const [barcodeInput, setBarcodeInput] = useState('');
    const [showFullForm, setShowFullForm] = useState(false);
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [cameraStream, setCameraStream] = useState(null);
    const [showOnlyInStock, setShowOnlyInStock] = useState(false);
    const [sortBy, setSortBy] = useState('name'); // 'name', 'price', 'category', 'barcode'
    const [sortDir, setSortDir] = useState('asc');
    const [formData, setFormData] = useState({
        name: '',
        barcode: '',
        description: '',
        price: '',
        category: ''
    });
    const [stockQuantity, setStockQuantity] = useState('1');
    const barcodeInputRef = useRef(null);
    
    // Category management state
    const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
    const [categories, setCategories] = useState([]);
    const [editingCategory, setEditingCategory] = useState(null);
    const [newCategoryName, setNewCategoryName] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null); // For viewing products
    const [categoryProducts, setCategoryProducts] = useState([]);
    
    // Category dropdown state for product form
    const [allCategories, setAllCategories] = useState([]);
    const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
    const [categoryFilter, setCategoryFilter] = useState('');

    // Open scanner
    const openScanner = () => {
        setIsScannerOpen(true);
    };

    // Close scanner and stop camera
    const closeScanner = () => {
        if (cameraStream) {
            cameraStream.getTracks().forEach(t => t.stop());
            setCameraStream(null);
        }
        setIsScannerOpen(false);
    };

    // Handle barcode from scanner
    const handleScannedBarcode = async (barcode) => {
        // Stop camera stream
        if (cameraStream) {
            cameraStream.getTracks().forEach(t => t.stop());
            setCameraStream(null);
        }
        setIsScannerOpen(false);
        setBarcodeInput(barcode);
        
        // Trigger the same logic as Enter key
        try {
            const response = await getProductByBarcode(barcode);
            if (response.data) {
                // Product exists - add to stock
                await addToStock({
                    product_id: response.data.id,
                    quantity: parseInt(stockQuantity),
                    pallet_location: `PAL ${currentPallet}`
                });
                toast.success(`"${response.data.name}" adăugat în PAL ${currentPallet}`);
                setBarcodeInput('');
                loadProducts(search);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                // New product - show form
                setFormData(prev => ({ ...prev, barcode: barcode }));
                setShowFullForm(true);
            }
        }
    };

    useEffect(() => {
        loadProducts();
    }, []);

    // Handle edit parameter from URL (from Stock General)
    useEffect(() => {
        const editId = searchParams.get('edit');
        if (editId && products.length > 0) {
            const productToEdit = products.find(p => p.id === editId);
            if (productToEdit) {
                openModal(productToEdit);
                // Clear the URL parameter
                setSearchParams({});
            }
        }
    }, [searchParams, products]);

    // Close category dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (showCategoryDropdown && !e.target.closest('[data-category-dropdown]')) {
                setShowCategoryDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showCategoryDropdown]);

    const loadProducts = async (searchTerm = '') => {
        try {
            setLoading(true);
            const response = await getProducts(searchTerm);
            setProducts(response.data);
            
            // Load locations for all products
            const locationsResponse = await api.get('/products/locations');
            setProductLocations(locationsResponse.data);
        } catch (err) {
            toast.error('Eroare la încărcarea produselor');
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        loadProducts(search);
    };

    // Load all categories for dropdown
    const loadAllCategories = async () => {
        try {
            const response = await api.get('/categories');
            setAllCategories(response.data);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const openModal = (product = null) => {
        loadAllCategories(); // Load categories for dropdown
        if (product) {
            setEditingProduct(product);
            setFormData({
                name: product.name,
                barcode: product.barcode || '',
                description: product.description || '',
                price: product.price?.toString() || '',
                category: product.category || ''
            });
            setShowFullForm(true);
            setBarcodeInput('');
        } else {
            setEditingProduct(null);
            setFormData({
                name: '',
                barcode: '',
                description: '',
                price: '',
                category: ''
            });
            setShowFullForm(false);
            setBarcodeInput('');
            setStockQuantity('1');
        }
        setIsModalOpen(true);
        setShowCategoryDropdown(false);
        setCategoryFilter('');
        
        setTimeout(() => {
            if (barcodeInputRef.current && !product) {
                barcodeInputRef.current.focus();
            }
        }, 100);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingProduct(null);
        setShowFullForm(false);
        setBarcodeInput('');
        setFormData({
            name: '',
            barcode: '',
            description: '',
            price: '',
            category: ''
        });
    };

    // Category management functions
    const loadCategories = async () => {
        try {
            const response = await api.get('/categories/details');
            setCategories(response.data);
        } catch (error) {
            toast.error('Eroare la încărcarea categoriilor');
        }
    };

    const openCategoryModal = () => {
        loadCategories();
        setSelectedCategory(null);
        setCategoryProducts([]);
        setIsCategoryModalOpen(true);
    };

    const viewCategoryProducts = (categoryName) => {
        const productsInCategory = products.filter(p => p.category === categoryName);
        setCategoryProducts(productsInCategory);
        setSelectedCategory(categoryName);
    };

    const backToCategories = () => {
        setSelectedCategory(null);
        setCategoryProducts([]);
    };

    const startEditCategory = (cat) => {
        setEditingCategory(cat.name);
        setNewCategoryName(cat.name);
    };

    const cancelEditCategory = () => {
        setEditingCategory(null);
        setNewCategoryName('');
    };

    const saveCategory = async (oldName) => {
        if (!newCategoryName.trim()) {
            toast.error('Numele categoriei nu poate fi gol');
            return;
        }
        if (newCategoryName.trim() === oldName) {
            cancelEditCategory();
            return;
        }
        try {
            const response = await api.post('/categories/rename', {
                old_name: oldName,
                new_name: newCategoryName.trim()
            });
            toast.success(response.data.message);
            cancelEditCategory();
            loadCategories();
            loadProducts(search);
        } catch (error) {
            toast.error('Eroare la redenumirea categoriei');
        }
    };

    const deleteCategory = async (categoryName) => {
        if (!confirm(`Sigur vrei să ștergi categoria "${categoryName}"? Produsele vor rămâne fără categorie.`)) {
            return;
        }
        try {
            const response = await api.delete(`/categories/${encodeURIComponent(categoryName)}`);
            toast.success(response.data.message);
            loadCategories();
            loadProducts(search);
        } catch (error) {
            toast.error('Eroare la ștergerea categoriei');
        }
    };

    const handleBarcodeKeyDown = async (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            
            if (!barcodeInput.trim()) {
                // Empty barcode - show manual form with auto-generated barcode
                setFormData(prev => ({ ...prev, barcode: generateBarcode() }));
                setShowFullForm(true);
                return;
            }
            
            try {
                const response = await getProductByBarcode(barcodeInput.trim());
                if (response.data) {
                    await addToStock({
                        product_id: response.data.id,
                        quantity: parseInt(stockQuantity),
                        pallet_location: `PAL ${currentPallet}`
                    });
                    toast.success(`"${response.data.name}" adăugat în PAL ${currentPallet}`);
                    setBarcodeInput('');
                    setStockQuantity('1');
                    loadProducts(search);
                    
                    setTimeout(() => {
                        if (barcodeInputRef.current) {
                            barcodeInputRef.current.focus();
                        }
                    }, 100);
                }
            } catch (err) {
                if (err.response?.status === 404) {
                    setFormData(prev => ({ ...prev, barcode: barcodeInput.trim() }));
                    setShowFullForm(true);
                } else {
                    toast.error('Eroare la căutarea produsului');
                }
            }
        }
    };

    const handleManualEntry = () => {
        // Generate barcode when entering manual mode
        setFormData(prev => ({ ...prev, barcode: generateBarcode() }));
        setShowFullForm(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Auto-generate barcode if empty
        const finalBarcode = formData.barcode.trim() || generateBarcode();
        
        const data = {
            name: formData.name,
            barcode: finalBarcode,
            description: formData.description || null,
            price: parseFloat(formData.price) || 0,
            category: formData.category || null
        };

        try {
            if (editingProduct) {
                await updateProduct(editingProduct.id, data);
                toast.success('Produs actualizat cu succes');
                closeModal();
            } else {
                const productResponse = await createProduct(data);
                const newProduct = productResponse.data;
                
                await addToStock({
                    product_id: newProduct.id,
                    quantity: parseInt(stockQuantity),
                    pallet_location: `PAL ${currentPallet}`
                });
                
                toast.success(`"${newProduct.name}" adăugat în PAL ${currentPallet}`);
                
                setFormData({
                    name: '',
                    barcode: '',
                    description: '',
                    price: '',
                    category: ''
                });
                setShowFullForm(false);
                setBarcodeInput('');
                setStockQuantity('1');
                
                setTimeout(() => {
                    if (barcodeInputRef.current) {
                        barcodeInputRef.current.focus();
                    }
                }, 100);
            }
            loadProducts(search);
        } catch (err) {
            toast.error('Eroare la salvarea produsului');
            console.error(err);
        }
    };

    const handleDelete = async (product) => {
        if (!window.confirm(`Sigur dorești să ștergi "${product.name}"?`)) return;
        
        try {
            await deleteProduct(product.id);
            toast.success('Produs șters cu succes');
            loadProducts(search);
        } catch (err) {
            toast.error('Eroare la ștergerea produsului');
            console.error(err);
        }
    };

    return (
        <div data-testid="products-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                <div>
                    <h1 className="text-3xl md:text-4xl font-bold text-zinc-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                        Produse
                    </h1>
                    <p className="text-zinc-500 mt-1">Gestionează catalogul de produse</p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={openCategoryModal}
                        data-testid="manage-categories-button"
                        variant="outline"
                        className="h-12 px-4"
                    >
                        <Tags size={20} className="mr-2" />
                        Categorii
                    </Button>
                    <Button
                        onClick={() => openModal()}
                        data-testid="add-product-button"
                        className="bg-zinc-900 hover:bg-zinc-800 text-white h-12 px-6"
                    >
                        <Plus size={20} className="mr-2" />
                        Adaugă Produs
                    </Button>
                </div>
            </div>

            {/* Search */}
            <form onSubmit={handleSearch} className="mb-4">
                <div className="relative">
                    <Search size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                    <Input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Caută după nume sau cod de bare..."
                        data-testid="product-search-input"
                        className="pl-10 h-12 bg-white"
                    />
                </div>
            </form>
            
            {/* Toggle to show only products in stock */}
            <div className="flex items-center justify-between mb-4 bg-white border border-zinc-200 rounded-lg p-3">
                <span className="text-sm text-zinc-600">Arată doar produse în stoc</span>
                <button
                    onClick={() => setShowOnlyInStock(!showOnlyInStock)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${
                        showOnlyInStock ? 'bg-emerald-500' : 'bg-zinc-300'
                    }`}
                >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${
                        showOnlyInStock ? 'left-7' : 'left-1'
                    }`} />
                </button>
            </div>

            {/* Products Grid */}
            {loading ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                    {[1, 2, 3, 4, 5, 6].map((i) => (
                        <div key={i} className="bg-white border border-zinc-200 rounded-lg p-4">
                            <Skeleton className="h-4 w-3/4 mb-3" />
                            <Skeleton className="h-3 w-1/2 mb-2" />
                            <Skeleton className="h-6 w-20" />
                        </div>
                    ))}
                </div>
            ) : products.length === 0 ? (
                <div className="empty-state bg-white border border-zinc-200 rounded-lg">
                    <Package size={64} className="text-zinc-300" />
                    <h3 className="text-lg font-medium text-zinc-900 mt-4">Niciun produs</h3>
                    <p className="text-zinc-500 mt-1">Adaugă primul produs în catalog</p>
                    <Button
                        onClick={() => openModal()}
                        className="mt-4 bg-zinc-900 hover:bg-zinc-800"
                    >
                        <Plus size={20} className="mr-2" />
                        Adaugă Produs
                    </Button>
                </div>
            ) : (
                <>
                    {/* Sort Options */}
                    <div className="flex flex-wrap items-center gap-1 mb-3">
                        <span className="text-xs text-zinc-500 mr-1">Sortare:</span>
                        {[
                            { field: 'name', label: 'Nume' },
                            { field: 'price', label: 'Preț' },
                            { field: 'category', label: 'Categorie' },
                            { field: 'stock', label: 'Stoc' }
                        ].map(({ field, label }) => (
                            <button
                                key={field}
                                onClick={() => {
                                    if (sortBy === field) {
                                        setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                                    } else {
                                        setSortBy(field);
                                        setSortDir(field === 'stock' ? 'desc' : 'asc');
                                    }
                                }}
                                className={`flex items-center gap-0.5 text-xs font-medium px-1.5 py-0.5 rounded ${
                                    sortBy === field ? 'bg-zinc-200 text-zinc-800' : 'text-zinc-500 hover:bg-zinc-100'
                                }`}
                            >
                                {label}
                                {sortBy === field ? (
                                    sortDir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />
                                ) : (
                                    <ArrowUpDown size={10} className="opacity-50" />
                                )}
                            </button>
                        ))}
                    </div>
                    
                    <div className="bg-white border border-zinc-200 rounded-lg divide-y">
                    {[...products]
                        .filter(product => {
                            if (!showOnlyInStock) return true;
                            const loc = productLocations[product.id] || {};
                            const totalQty = (loc.depozit_qty || 0) + (loc.store_qty || 0) + (loc.reserved_qty || 0);
                            return totalQty > 0;
                        })
                        .sort((a, b) => {
                            let valA, valB;
                            const locA = productLocations[a.id] || {};
                            const locB = productLocations[b.id] || {};
                            const stockA = (locA.depozit_qty || 0) + (locA.store_qty || 0) + (locA.reserved_qty || 0);
                            const stockB = (locB.depozit_qty || 0) + (locB.store_qty || 0) + (locB.reserved_qty || 0);
                            
                            switch (sortBy) {
                                case 'name':
                                    valA = (a.name || '').toLowerCase();
                                    valB = (b.name || '').toLowerCase();
                                    break;
                                case 'price':
                                    valA = a.price || 0;
                                    valB = b.price || 0;
                                    break;
                                case 'category':
                                    valA = (a.category || '').toLowerCase();
                                    valB = (b.category || '').toLowerCase();
                                    break;
                                case 'stock':
                                    valA = stockA;
                                    valB = stockB;
                                    break;
                                default:
                                    valA = (a.name || '').toLowerCase();
                                    valB = (b.name || '').toLowerCase();
                            }
                            if (sortDir === 'asc') {
                                return valA < valB ? -1 : valA > valB ? 1 : 0;
                            } else {
                                return valA > valB ? -1 : valA < valB ? 1 : 0;
                            }
                        })
                        .map((product) => {
                        const loc = productLocations[product.id] || {};
                        const depozitQty = loc.depozit_qty || 0;
                        const storeQty = loc.store_qty || 0;
                        const reservedQty = loc.reserved_qty || 0;
                        const totalQty = depozitQty + storeQty + reservedQty;
                        
                        return (
                            <div
                                key={product.id}
                                data-testid={`product-card-${product.id}`}
                                className="px-2 py-1 hover:bg-zinc-50 transition-colors"
                            >
                                <div className="flex items-center gap-2">
                                    {/* Product Info */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-1.5">
                                            <span className="font-medium text-sm text-zinc-900 truncate">{product.name}</span>
                                            {product.category && (
                                                <span className="text-[10px] bg-violet-100 text-violet-700 px-1 py-0.5 rounded hidden sm:inline">
                                                    {product.category}
                                                </span>
                                            )}
                                            <span className="text-xs text-zinc-500">{product.price?.toFixed(0) || 0} RON</span>
                                        </div>
                                    </div>
                                    
                                    {/* Stock Summary - Compact */}
                                    <div className="flex items-center gap-1.5 text-xs">
                                        <div className="flex items-center gap-0.5 text-amber-600" title="Depozit">
                                            <Warehouse size={12} />
                                            <span className="font-semibold w-5 text-right">{depozitQty}</span>
                                        </div>
                                        <div className="flex items-center gap-0.5 text-emerald-600" title="Magazin">
                                            <Store size={12} />
                                            <span className="font-semibold w-5 text-right">{storeQty}</span>
                                        </div>
                                        <div className="flex items-center gap-0.5 text-violet-600" title="Comenzi">
                                            <BookmarkCheck size={12} />
                                            <span className="font-semibold w-5 text-right">{reservedQty}</span>
                                        </div>
                                    </div>
                                    
                                    {/* Actions */}
                                    <div className="flex">
                                        <button
                                            onClick={() => openModal(product)}
                                            className="p-1 text-zinc-400 hover:text-zinc-600 hover:bg-zinc-100 rounded"
                                        >
                                            <Edit2 size={14} />
                                        </button>
                                        <button
                                            onClick={() => handleDelete(product)}
                                            className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded"
                                        >
                                            <Trash2 size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                    </div>
                </>
            )}

            {/* Add/Edit Modal */}
            <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            {editingProduct ? 'Editează Produs' : 'Adaugă Produs Nou'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {!editingProduct && !showFullForm ? (
                        <div className="space-y-4 mt-4">
                            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                                <Label className="text-amber-700">Palet Curent</Label>
                                <div className="flex items-center gap-3 mt-2">
                                    <span className="text-2xl font-bold text-amber-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                                        PAL
                                    </span>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={currentPallet}
                                        onChange={(e) => setCurrentPallet(e.target.value)}
                                        data-testid="current-pallet-input"
                                        className="w-20 h-12 text-xl font-bold text-center bg-white"
                                    />
                                </div>
                                <p className="text-xs text-amber-600 mt-2">Modifică când treci la alt palet</p>
                            </div>
                            
                            <div>
                                <Label htmlFor="barcode-scan" className="flex items-center gap-2">
                                    <ScanBarcode size={18} />
                                    Cod de Bare
                                </Label>
                                <div className="flex gap-2 mt-1">
                                    <Input
                                        id="barcode-scan"
                                        ref={barcodeInputRef}
                                        value={barcodeInput}
                                        onChange={(e) => setBarcodeInput(e.target.value)}
                                        onKeyDown={handleBarcodeKeyDown}
                                        data-testid="barcode-scan-input"
                                        placeholder="Scanează sau tastează codul..."
                                        className="h-14 text-lg font-mono flex-1"
                                        autoFocus
                                    />
                                    <Button
                                        type="button"
                                        onClick={openScanner}
                                        className="h-14 w-14 bg-[#E11D48] hover:bg-[#BE123C]"
                                        title="Deschide camera"
                                    >
                                        <Camera size={24} />
                                    </Button>
                                </div>
                                <p className="text-xs text-zinc-500 mt-2">
                                    Scanează codul → produs adăugat automat în PAL {currentPallet}<br/>
                                    <strong>Enter gol</strong> → introducere manuală (cod generat automat)
                                </p>
                            </div>
                            
                            <div>
                                <Label htmlFor="quick-qty">Cantitate</Label>
                                <Input
                                    id="quick-qty"
                                    type="number"
                                    min="1"
                                    value={stockQuantity}
                                    onChange={(e) => setStockQuantity(e.target.value)}
                                    data-testid="quick-quantity-input"
                                    className="mt-1"
                                />
                            </div>
                            
                            <div className="pt-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={handleManualEntry}
                                    data-testid="manual-entry-button"
                                    className="w-full"
                                >
                                    Introducere Manuală (produs nou)
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            {!editingProduct && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-center justify-between">
                                    <div>
                                        <span className="text-sm text-amber-700">Se adaugă în:</span>
                                        <span className="ml-2 font-bold text-amber-900">PAL {currentPallet}</span>
                                    </div>
                                    <Input
                                        type="number"
                                        min="1"
                                        value={stockQuantity}
                                        onChange={(e) => setStockQuantity(e.target.value)}
                                        className="w-20 h-9 text-center"
                                        data-testid="form-quantity-input"
                                    />
                                    <span className="text-sm text-amber-700">buc.</span>
                                </div>
                            )}
                            
                            <div>
                                <Label htmlFor="barcode">Cod de Bare (auto-generat dacă gol)</Label>
                                <Input
                                    id="barcode"
                                    value={formData.barcode}
                                    onChange={(e) => setFormData({ ...formData, barcode: e.target.value })}
                                    data-testid="product-barcode-input"
                                    placeholder="Se generează automat..."
                                    className="mt-1 font-mono"
                                />
                            </div>
                            
                            <div>
                                <Label htmlFor="name">Nume Produs *</Label>
                                <Input
                                    id="name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    data-testid="product-name-input"
                                    placeholder="Ex: Set pahare sticlă"
                                    required
                                    className="mt-1"
                                />
                            </div>
                            
                            <div>
                                <Label htmlFor="price">Preț Estimat (RON)</Label>
                                <Input
                                    id="price"
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={formData.price}
                                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                                    data-testid="product-price-input"
                                    placeholder="0.00"
                                    className="mt-1"
                                />
                            </div>
                            
                            <div data-category-dropdown>
                                <Label htmlFor="category">Categorie</Label>
                                <div className="relative mt-1">
                                    <Input
                                        id="category"
                                        value={formData.category}
                                        onChange={(e) => {
                                            setFormData({ ...formData, category: e.target.value });
                                            setCategoryFilter(e.target.value);
                                            setShowCategoryDropdown(true);
                                        }}
                                        onFocus={() => setShowCategoryDropdown(true)}
                                        data-testid="product-category-input"
                                        placeholder="Caută sau selectează categorie..."
                                        autoComplete="off"
                                    />
                                    {showCategoryDropdown && (
                                        <div className="absolute z-50 w-full mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                                            {allCategories
                                                .filter(cat => 
                                                    !categoryFilter || 
                                                    cat.toLowerCase().includes(categoryFilter.toLowerCase())
                                                )
                                                .map(cat => (
                                                    <button
                                                        key={cat}
                                                        type="button"
                                                        onClick={() => {
                                                            setFormData({ ...formData, category: cat });
                                                            setShowCategoryDropdown(false);
                                                            setCategoryFilter('');
                                                        }}
                                                        className={`w-full text-left px-3 py-2 hover:bg-zinc-100 text-sm ${
                                                            formData.category === cat ? 'bg-emerald-50 text-emerald-700 font-medium' : ''
                                                        }`}
                                                    >
                                                        {cat}
                                                    </button>
                                                ))
                                            }
                                            {allCategories.filter(cat => 
                                                !categoryFilter || 
                                                cat.toLowerCase().includes(categoryFilter.toLowerCase())
                                            ).length === 0 && (
                                                <div className="px-3 py-2 text-sm text-zinc-500">
                                                    Nicio categorie găsită. Poți crea una nouă.
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <p className="text-xs text-zinc-500 mt-1">
                                    Click pentru a selecta sau scrie pentru categorie nouă
                                </p>
                            </div>
                            
                            <div>
                                <Label htmlFor="description">Descriere</Label>
                                <Input
                                    id="description"
                                    value={formData.description}
                                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                    data-testid="product-description-input"
                                    placeholder="Descriere opțională"
                                    className="mt-1"
                                />
                            </div>
                            
                            <div className="flex gap-3 pt-4">
                                {!editingProduct && (
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setShowFullForm(false)}
                                        className="flex-1"
                                    >
                                        Înapoi
                                    </Button>
                                )}
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={closeModal}
                                    className={editingProduct ? "flex-1" : ""}
                                >
                                    {editingProduct ? "Anulează" : "Închide"}
                                </Button>
                                <Button
                                    type="submit"
                                    data-testid="save-product-button"
                                    className="flex-1 bg-zinc-900 hover:bg-zinc-800"
                                >
                                    {editingProduct ? 'Salvează' : 'Adaugă în Stock'}
                                </Button>
                            </div>
                        </form>
                    )}
                </DialogContent>
            </Dialog>
            
            {/* Barcode Scanner */}
            <BarcodeScanner
                isOpen={isScannerOpen}
                onClose={closeScanner}
                onScan={handleScannedBarcode}
            />

            {/* Category Management Modal */}
            <Dialog open={isCategoryModalOpen} onOpenChange={setIsCategoryModalOpen}>
                <DialogContent className="sm:max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
                    <DialogHeader>
                        <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>
                            <Tags className="inline mr-2" size={20} />
                            {selectedCategory ? `${selectedCategory}` : 'Gestionare Categorii'}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedCategory ? (
                        /* Products in selected category */
                        <div className="mt-4 overflow-y-auto flex-1">
                            <Button 
                                variant="outline" 
                                size="sm" 
                                onClick={backToCategories}
                                className="mb-3"
                            >
                                ← Înapoi la categorii
                            </Button>
                            
                            {categoryProducts.length === 0 ? (
                                <p className="text-center text-zinc-500 py-8">Nu există produse în această categorie</p>
                            ) : (
                                <div className="space-y-1">
                                    {categoryProducts.map((product) => {
                                        const loc = productLocations[product.id] || {};
                                        const totalQty = (loc.depozit_qty || 0) + (loc.store_qty || 0) + (loc.reserved_qty || 0);
                                        return (
                                            <div 
                                                key={product.id} 
                                                className="flex items-center justify-between p-2 bg-zinc-50 rounded border border-zinc-100 hover:bg-zinc-100"
                                            >
                                                <div className="flex-1 min-w-0">
                                                    <p className="font-medium text-sm text-zinc-900 truncate">{product.name}</p>
                                                    <p className="text-xs text-zinc-500">{product.price?.toFixed(0) || 0} RON</p>
                                                </div>
                                                <div className="flex items-center gap-2 text-xs">
                                                    <span className="text-amber-600">{loc.depozit_qty || 0}</span>
                                                    <span className="text-emerald-600">{loc.store_qty || 0}</span>
                                                    <span className="text-violet-600">{loc.reserved_qty || 0}</span>
                                                    <span className="font-semibold text-zinc-700 ml-1">= {totalQty}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                            <p className="text-xs text-zinc-400 mt-3 text-center">
                                {categoryProducts.length} produse în "{selectedCategory}"
                            </p>
                        </div>
                    ) : (
                        /* Categories list */
                        <div className="mt-4 overflow-y-auto flex-1 pr-2">
                            {categories.length === 0 ? (
                                <p className="text-center text-zinc-500 py-8">Nu există categorii definite</p>
                            ) : (
                                <div className="space-y-2">
                                    {categories.map((cat) => (
                                        <div 
                                            key={cat.name} 
                                            className="flex items-center justify-between gap-2 p-3 bg-zinc-50 rounded-lg border border-zinc-200 hover:bg-zinc-100 transition-colors"
                                        >
                                            {editingCategory === cat.name ? (
                                                <div className="flex-1 flex items-center gap-2">
                                                    <Input
                                                        value={newCategoryName}
                                                        onChange={(e) => setNewCategoryName(e.target.value)}
                                                        className="flex-1 h-9"
                                                        autoFocus
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') saveCategory(cat.name);
                                                            if (e.key === 'Escape') cancelEditCategory();
                                                        }}
                                                    />
                                                    <Button
                                                        size="sm"
                                                        onClick={() => saveCategory(cat.name)}
                                                        className="bg-emerald-600 hover:bg-emerald-700 h-9"
                                                    >
                                                        Salvează
                                                    </Button>
                                                    <Button
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={cancelEditCategory}
                                                        className="h-9"
                                                    >
                                                        <X size={16} />
                                                    </Button>
                                                </div>
                                            ) : (
                                                <>
                                                    <button 
                                                        onClick={() => viewCategoryProducts(cat.name)}
                                                        className="flex-1 min-w-0 text-left"
                                                    >
                                                        <p className="font-medium text-zinc-900 truncate">{cat.name}</p>
                                                        <p className="text-xs text-zinc-500">{cat.count} produse → click pentru detalii</p>
                                                    </button>
                                                    <div className="flex items-center gap-1">
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => startEditCategory(cat)}
                                                            className="h-8 px-2 text-zinc-600 hover:text-zinc-900"
                                                            title="Editează/Redenumește"
                                                        >
                                                            <Edit2 size={16} />
                                                        </Button>
                                                        <Button
                                                            size="sm"
                                                            variant="ghost"
                                                            onClick={() => deleteCategory(cat.name)}
                                                            className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                            title="Șterge categoria"
                                                        >
                                                            <Trash2 size={16} />
                                                        </Button>
                                                    </div>
                                                </>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    <div className="mt-4 pt-4 border-t border-zinc-200">
                        <p className="text-xs text-zinc-500 mb-3">
                            <strong>Tip:</strong> Pentru a muta un produs la altă categorie, editează produsul individual și schimbă câmpul "Categorie".
                        </p>
                        <Button
                            variant="outline"
                            onClick={() => setIsCategoryModalOpen(false)}
                            className="w-full"
                        >
                            Închide
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Products;
